/**
 * HourlyMeter — live daily production meter for hourly workers.
 *
 * A thin bar at the top of the hourly worker's screen that fills red → green as
 * the value of the jobs they've done today grows. Two milestones:
 *   1. Production ≥ daily wage   → "Salary covered" (break-even ✅)
 *   2. Production ≥ goal         → 🎉 Congratulations (day won)
 *
 * Progress is shown as a PERCENT of the day's goal — never in dollars, so the
 * worker doesn't see what the company charges/pays. Data comes from
 * GET /api/jobs/my-production (markup stays server-side; the component derives
 * percentages from the production/wage/goal it returns). Refreshes on mount,
 * when `reloadSignal` changes (e.g. after a job submit), and every 45s.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const NAVY = '#1e3a4a';

// Red → amber as the worker climbs toward their salary; GREEN only once they
// reach it. Below break-even it stays red/amber — never green early.
function meterColor(production, wage) {
  if (production >= wage) return '#16a34a';               // salary covered → green
  const f = wage > 0 ? Math.max(0, Math.min(1, production / wage)) : 0;
  return `hsl(${Math.round(45 * f)}, 82%, 47%)`;          // 0 = red → 45 = amber
}

export default function HourlyMeter({ user, reloadSignal, date }) {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!user || user.type !== 'hourly') return;
    api.getMyProduction(date)
      .then(d => setData(d))
      .catch(() => {});
  }, [user, date]);

  // On mount + whenever the parent bumps reloadSignal (e.g. after a submit).
  useEffect(() => { load(); }, [load, reloadSignal]);

  // Live refresh every 45s.
  useEffect(() => {
    if (!user || user.type !== 'hourly') return;
    const id = setInterval(load, 45_000);
    return () => clearInterval(id);
  }, [load, user]);

  // Only hourly workers get the meter — but once that's true, ALWAYS render
  // (even while the first fetch is in flight or if it errored) so it never
  // silently disappears. Missing data just falls back to a 0% / default bar.
  if (!user || user.type !== 'hourly') return null;

  const { production = 0, wage = 228, goal = 278, jobCount = 0 } = data || {};

  // Everything the worker sees is a PERCENT of the goal — no dollar amounts.
  const pct        = goal > 0 ? Math.round((production / goal) * 100) : 0; // can exceed 100
  const barFrac    = Math.min(goal > 0 ? production / goal : 0, 1);        // bar fill 0..1
  const wageMark   = Math.min(goal > 0 ? wage / goal : 0, 1) * 100;        // tick position %
  const hitWage    = production >= wage;
  const hitGoal    = production >= goal;

  // Status line + accent colour for the milestone callout.
  let statusText, statusColor;
  if (hitGoal) {
    statusText  = "🎉 Congratulations! You smashed today's goal!";
    statusColor = '#15803d';
  } else if (hitWage) {
    statusText  = '✅ Salary covered — push for the goal!';
    statusColor = '#16a34a';
  } else {
    statusText  = "Keep going — cover your salary to win the day.";
    statusColor = '#b45309';
  }

  return (
    <div className="no-print" style={{
      background: hitGoal ? '#f0fdf4' : '#fff',
      borderBottom: `2px solid ${hitGoal ? '#16a34a' : '#e5e7eb'}`,
      padding: '10px 20px',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Header row: title + live dot + percent */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: NAVY }}>📊 Today's production</span>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', background: '#16a34a',
            boxShadow: '0 0 0 0 rgba(22,163,74,0.6)', animation: 'meterPulse 1.8s infinite',
          }} />
          <span style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 800, color: statusColor }}>
            {pct}%
          </span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            of goal
            {jobCount > 0 && <> · {jobCount} job{jobCount !== 1 ? 's' : ''}</>}
          </span>
        </div>

        {/* The meter bar */}
        <div style={{ position: 'relative', height: 16, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0,
            width: `${barFrac * 100}%`,
            background: meterColor(production, wage),
            borderRadius: 999,
            transition: 'width 0.5s ease, background 0.5s ease',
          }} />
          {/* Salary milestone tick */}
          <div style={{
            position: 'absolute', top: -2, bottom: -2, left: `${wageMark}%`,
            width: 2, background: hitWage ? '#15803d' : '#475569',
          }} />
        </div>

        {/* Scale labels under the bar (names only — no dollar values) */}
        <div style={{ position: 'relative', height: 16, marginTop: 2, fontSize: 10.5, color: '#6b7280' }}>
          <span style={{ position: 'absolute', left: 0 }}>0%</span>
          <span style={{
            position: 'absolute', left: `${wageMark}%`, transform: 'translateX(-50%)',
            fontWeight: 700, color: hitWage ? '#15803d' : '#475569',
          }}>
            Salary
          </span>
          <span style={{ position: 'absolute', right: 0, fontWeight: 700, color: hitGoal ? '#15803d' : '#475569' }}>
            🎯 Goal
          </span>
        </div>

        {/* Status line */}
        <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 700, color: statusColor }}>
          {statusText}
        </div>
      </div>

      {/* Keyframes for the live dot (scoped, harmless if duplicated) */}
      <style>{`@keyframes meterPulse {
        0%   { box-shadow: 0 0 0 0 rgba(22,163,74,0.55); }
        70%  { box-shadow: 0 0 0 7px rgba(22,163,74,0); }
        100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
      }`}</style>
    </div>
  );
}
